import { Table } from '@tanstack/react-table'
import { useLayoutEffect, useState } from 'react'

export function useColumnWidths<TData>(
  table: Table<TData>,
  headerMeasureRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>,
  cellMeasureRefs: React.MutableRefObject<Record<string, HTMLDivElement[]>>
) {
  const [computedColWidths, setComputedColWidths] = useState<Record<string, number>>({})

  useLayoutEffect(() => {
    const next: Record<string, number> = { ...computedColWidths }
    const horizontalPaddingPx = 32 // px-4 left + px-4 right on th/td
    
    table.getAllLeafColumns().forEach((col) => {
      if (col.columnDef.size) return
      
      let maxContentWidth = 0
      
      // Measure header
      const headEl = headerMeasureRefs.current[col.id]
      if (headEl) {
        maxContentWidth = Math.max(maxContentWidth, Math.ceil(headEl.scrollWidth))
      }
      
      // Measure cells
      const cellEls = cellMeasureRefs.current[col.id] || []
      for (const el of cellEls) {
        if (!el) continue
        maxContentWidth = Math.max(maxContentWidth, Math.ceil(el.scrollWidth))
      }
      
      if (maxContentWidth > 0) {
        const desired = maxContentWidth + horizontalPaddingPx
        next[col.id] = Math.max(next[col.id] ?? 0, desired)
      }
    })

    const changed =
      Object.keys(next).length !== Object.keys(computedColWidths).length ||
      Object.keys(next).some((k) => next[k] !== computedColWidths[k])

    if (changed) setComputedColWidths(next)
  }, [computedColWidths, table, headerMeasureRefs, cellMeasureRefs])

  return computedColWidths
}

